import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WishlistContext = createContext(null);

export const useWishlist = () => {
    const context = useContext(WishlistContext);
    if (!context) {
        throw new Error('useWishlist must be used within a WishlistProvider');
    }
    return context;
};

export const WishlistProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [wishlistIds, setWishlistIds] = useState(new Set());
    const [loading, setLoading] = useState(false);

    const fetchWishlistIds = useCallback(async () => {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            setWishlistIds(new Set());
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/wishlist`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                const ids = (data.products || []).map(p => p.product_id);
                setWishlistIds(new Set(ids));
            } else {
                setWishlistIds(new Set());
            }
        } catch (error) {
            console.error('Failed to fetch wishlist:', error);
            setWishlistIds(new Set());
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchWishlistIds();
        } else {
            setWishlistIds(new Set());
        }
    }, [isAuthenticated, fetchWishlistIds]);

    const addToWishlist = useCallback(async (productId) => {
        const token = localStorage.getItem('auth_token');
        if (!token) return false;

        try {
            const response = await fetch(`${API_URL}/wishlist/${productId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                setWishlistIds(prev => new Set([...prev, productId]));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to add to wishlist:', error);
            return false;
        }
    }, []);

    const removeFromWishlist = useCallback(async (productId) => {
        const token = localStorage.getItem('auth_token');
        if (!token) return false;

        try {
            const response = await fetch(`${API_URL}/wishlist/${productId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                setWishlistIds(prev => {
                    const next = new Set(prev);
                    next.delete(productId);
                    return next;
                });
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to remove from wishlist:', error);
            return false;
        }
    }, []);

    const isInWishlist = useCallback((productId) => {
        return wishlistIds.has(productId);
    }, [wishlistIds]);

    const toggleWishlist = useCallback(async (productId) => {
        if (isInWishlist(productId)) {
            return await removeFromWishlist(productId);
        } else {
            return await addToWishlist(productId);
        }
    }, [isInWishlist, addToWishlist, removeFromWishlist]);

    const value = {
        wishlistIds,
        loading,
        isInWishlist,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
        refreshWishlist: fetchWishlistIds,
    };

    return (
        <WishlistContext.Provider value={value}>
            {children}
        </WishlistContext.Provider>
    );
};

export default WishlistContext;
